import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AdminService } from '../services/admin/admin.service';
import { HelpersService } from '../../shared/servicio/helpers/helpers.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-challenge',
  templateUrl: './challenge.component.html',
  styleUrls: ['./challenge.component.css']
})
export class ChallengeComponent implements OnInit, OnDestroy {

  @ViewChild('createTemplate') createTemplate: TemplateRef<any>;
  @ViewChild('editTemplate') editTemplate: TemplateRef<any>;
  @ViewChild('deleteTemplate') deleteTemplate: TemplateRef<any>;

  // Modal
  modalRef: BsModalRef;

  // Form
  challengeForm: FormGroup;
  submitted = false;

  // Loading y errores
  showLoading = true;
  preloader = false;
  div_error = 0;
  div_text_error = '';

  // Filtros
  criterioSelectorFC = this.fb.control('name');
  searchTextFC = this.fb.control('');
  statusFilterFC = this.fb.control('');
  challengeTypeFilterFC = this.fb.control('');

  // Estados de filtros
  isActiveON = true;
  isInactiveON = true;

  // Datos
  list_challenges: any[] = [];
  selectedChallenge: any = null;

  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Filtros aplicados
  activeFilter: boolean | undefined = undefined;
  searchFilter: any;
  statusFilter: any;
  challengeTypeFilter: any;

  // Imagen
  selectedImage: File | null = null;
  selectedImageBase64: string | null = null;
  imagePreview: string | null = null;
  imageError = '';

  // Opciones
  challengeTypeOptions = [
    { value: 'referrals', label: 'Referidos' },
    { value: 'effective_referrals', label: 'Referidos Efectivos' },
    { value: 'points', label: 'Puntos' },
    { value: 'consecutive_days', label: 'Días Consecutivos' },
  ];

  // Destroy subject
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalService: BsModalService,
    private adminService: AdminService,
    public helper: HelpersService
  ) {
    this.initForm();
    this.setupSearchFilters();
  }

  ngOnInit() {
    this.loadChallenges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Getter para acceso fácil a los controles del formulario
  get f() { return this.challengeForm.controls; }

  /**
   * Inicializar formulario
   */
  initForm() {
    this.challengeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      challenge_type: ['', [Validators.required]],
      target_value: ['', [Validators.required, Validators.min(1), Validators.max(99999)]],
      points_reward: ['', [Validators.required, Validators.min(1), Validators.max(99999)]],
      is_active: [true],
      is_monthly: [true]
    });
  }

  /**
   * Configurar filtros de búsqueda con debounce
   */
  setupSearchFilters() {
    this.searchTextFC.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.searchFilter = value || '';
        this.currentPage = 1;
        this.loadChallenges();
      });

    this.statusFilterFC.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.statusFilter = value || '';
        this.currentPage = 1;
        this.loadChallenges();
      });

    this.challengeTypeFilterFC.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.challengeTypeFilter = value || '';
        this.currentPage = 1;
        this.loadChallenges();
      });
  }

  /**
   * Cargar challenges
   */
  loadChallenges() {
    this.showLoading = true;

    const searchTerm = this.getSearchTerm();

    console.log('🔄 Cargando challenges...');

    this.adminService.listChallenges(
      this.currentPage,
      this.pageSize,
      this.activeFilter,
      this.challengeTypeFilter,
      searchTerm
    ).subscribe({
      next: (response) => {
        console.log('📋 Response received:', response);
        console.log('📋 Response type:', typeof response);
        console.log('📋 Is Array:', Array.isArray(response));
        console.log('📋 Response.results exists:', !!response.results);

        // Verificar si la respuesta es directamente un array o tiene una propiedad results
        if (Array.isArray(response)) {
          this.list_challenges = response;
          this.totalItems = response.length;
        } else if (response.results && Array.isArray(response.results)) {
          this.list_challenges = response.results;
          this.totalItems = response.count || response.results.length;
        } else {
          console.error('❌ Formato de respuesta inesperado:', response);
          this.list_challenges = [];
          this.totalItems = 0;
        }

        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        console.log('📋 Challenges loaded:', this.list_challenges.length);
        console.log('📋 First challenge:', this.list_challenges[0]);
        console.log('📋 Images in challenges:', this.list_challenges.map(c => ({
          name: c.name,
          hasImage: !!c.image,
          imagePreview: c.image ? c.image.substring(0, 50) + '...' : 'null'
        })));

        this.showLoading = false;
      },
      error: (error) => {
        console.error('❌ Error loading challenges:', error);
        this.showLoading = false;
        this.helper.modalError('Error al cargar los challenges');
      }
    });
  }

  /**
   * Obtener término de búsqueda basado en el criterio seleccionado
   */
  getSearchTerm(): string {
    if (!this.searchFilter) return '';

    const criterio = this.criterioSelectorFC.value;

    switch (criterio) {
      case 'name':
        return this.searchFilter;
      case 'description':
        return this.searchFilter;
      case 'challenge_type':
        return this.searchFilter;
      default:
        return this.searchFilter;
    }
  }

  /**
   * Toggle filtro activos
   */
  onActiveToggle() {
    this.isActiveON = !this.isActiveON;
    this.updateActiveFilter();
  }

  /**
   * Toggle filtro inactivos
   */
  onInactiveToggle() {
    this.isInactiveON = !this.isInactiveON;
    this.updateActiveFilter();
  }

  /**
   * Actualizar filtro de activos/inactivos
   */
  updateActiveFilter() {
    if (this.isActiveON && this.isInactiveON) {
      this.activeFilter = undefined; // Mostrar todos
    } else if (this.isActiveON) {
      this.activeFilter = true; // Solo activos
    } else if (this.isInactiveON) {
      this.activeFilter = false; // Solo inactivos
    } else {
      this.activeFilter = undefined; // Mostrar todos si ninguno está seleccionado
    }

    this.currentPage = 1;
    this.loadChallenges();
  }

  /**
   * Cambiar página
   */
  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadChallenges();
    }
  }

  /**
   * Abrir modal crear
   */
  openCreateModal() {
    this.resetForm();
    this.selectedChallenge = null;
    this.modalRef = this.modalService.show(this.createTemplate, {
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal editar
   */
  openEditModal(challenge: any) {
    this.resetForm();
    this.selectedChallenge = challenge;
    this.populateForm(challenge);
    this.modalRef = this.modalService.show(this.editTemplate, {
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal eliminar
   */
  openDeleteModal(challenge: any) {
    this.selectedChallenge = challenge;
    this.modalRef = this.modalService.show(this.deleteTemplate, {
      backdrop: 'static'
    });
  }

  /**
   * Cerrar modal
   */
  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  /**
   * Reset formulario
   */
  resetForm() {
    this.challengeForm.reset({
      is_active: true,
      is_monthly: true
    });
    this.submitted = false;
    this.div_error = 0;
    this.div_text_error = '';
    this.selectedImage = null;
    this.selectedImageBase64 = null;
    this.imagePreview = null;
    this.imageError = '';
  }

  /**
   * Poblar formulario con datos del challenge
   */
  populateForm(challenge: any) {
    this.challengeForm.patchValue({
      name: challenge.name,
      description: challenge.description,
      challenge_type: challenge.challenge_type,
      target_value: challenge.target_value,
      points_reward: challenge.points_reward,
      is_active: challenge.is_active,
      is_monthly: challenge.is_monthly
    });

    if (challenge.image) {
      this.imagePreview = challenge.image;
      // Si ya viene con el formato completo, usarlo directamente
      if (challenge.image.startsWith('data:image')) {
        this.selectedImageBase64 = challenge.image;
      } else {
        // Si es una URL u otro formato, mantener como está para preview
        this.selectedImageBase64 = null;
      }
      console.log('🖼️ Imagen cargada en formulario de edición:', !!challenge.image);
    }
  }

  /**
   * Submit formulario
   */
  onSubmit() {
    this.submitted = true;
    this.div_error = 0;

    console.log('🚀 === DEBUG SUBMIT COMPLETO ===');
    console.log('📋 Form values:', this.challengeForm.value);
    console.log('📋 Form valid:', this.challengeForm.valid);

    console.log('🖼️ === ESTADO DE LA IMAGEN ===');
    console.log('🖼️ selectedImage:', this.selectedImage);
    console.log('🖼️ selectedImageBase64 exists:', !!this.selectedImageBase64);
    console.log('🖼️ selectedImageBase64 length:', this.selectedImageBase64 ? this.selectedImageBase64.length : 0);
    console.log('🖼️ imagePreview exists:', !!this.imagePreview);
    console.log('🖼️ imageError:', this.imageError);

    if (this.challengeForm.invalid) {
      console.log('❌ Form is invalid, stopping submission');
      Object.keys(this.challengeForm.controls).forEach(key => {
        const control = this.challengeForm.get(key);
        if (control && control.invalid) {
          console.log(`❌ Field "${key}" is invalid:`, control.errors);
        }
      });
      return;
    }

    this.preloader = true;

    // IMPORTANTE: Crear objeto JSON simple, NO FormData
    const jsonData: any = {
      name: this.challengeForm.value.name,
      description: this.challengeForm.value.description,
      challenge_type: this.challengeForm.value.challenge_type,
      target_value: this.challengeForm.value.target_value,
      points_reward: this.challengeForm.value.points_reward,
      is_active: this.challengeForm.value.is_active,
      is_monthly: this.challengeForm.value.is_monthly
    };

    console.log('📦 === PREPARANDO JSON DATA ===');
    console.log('📦 Initial jsonData:', jsonData);

    // Agregar imagen como string base64 si existe
    if (this.selectedImageBase64) {
      jsonData.image = this.selectedImageBase64;
      console.log('✅ Imagen agregada al jsonData como string');
      console.log('📸 Base64 sample (first 100 chars):', this.selectedImageBase64.substring(0, 100) + '...');
      console.log('📏 Total base64 length:', this.selectedImageBase64.length);
    } else {
      console.log('⚠️ No hay selectedImageBase64 para enviar');
    }

    console.log('📦 Final jsonData keys:', Object.keys(jsonData));
    console.log('📦 Final jsonData image field exists:', 'image' in jsonData);
    console.log('📦 Final jsonData image value type:', typeof jsonData.image);
    console.log('📦 Final jsonData (sin mostrar base64 completo):', {
      ...jsonData,
      image: jsonData.image ? `[BASE64 STRING - ${jsonData.image.length} chars]` : undefined
    });

    const operation$ = this.selectedChallenge
      ? this.adminService.updateChallenge(this.selectedChallenge.id, jsonData)
      : this.adminService.createChallenge(jsonData);

    console.log('🌐 Enviando petición JSON al backend...');

    operation$.subscribe({
      next: (response) => {
        console.log('✅ Success response:', response);
        this.preloader = false;
        this.helper.modalSuccess(`¡Challenge ${this.selectedChallenge ? 'actualizado' : 'creado'} exitosamente!`);
        this.closeModal();

        setTimeout(() => {
          this.loadChallenges();
        }, 500);
      },
      error: (error) => {
        console.log('❌ Error response:', error);
        console.log('❌ Error body:', error.error);
        this.preloader = false;
        console.error('Error saving challenge:', error);
        this.div_error = 1;
        this.div_text_error = error.error.message || 'Error al guardar el challenge';
        this.helper.modalError(`Error al ${this.selectedChallenge ? 'actualizar' : 'crear'} el challenge`);
      }
    });
  }

  /**
   * Eliminar challenge
   */
  deleteChallenge() {
    if (!this.selectedChallenge) return;

    this.preloader = true;

    this.adminService.deleteChallenge(this.selectedChallenge.id).subscribe({
      next: () => {
        this.preloader = false;

        this.helper.modalSuccess('¡Challenge eliminado exitosamente!');

        this.closeModal();
        this.loadChallenges();
      },
      error: (error) => {
        this.preloader = false;
        console.error('Error deleting challenge:', error);

        this.helper.modalError('Error al eliminar el challenge');
      }
    });
  }

  /**
   * Toggle estado del challenge
   */
  toggleChallengeStatus(challenge: any) {
    const newStatus = !challenge.is_active;

    this.helper.modalConfirm(
      `¿Está seguro que desea ${newStatus ? 'activar' : 'desactivar'} este challenge?`
    ).then((result) => {
      if (result.value) {
        this.adminService.toggleChallengeStatus(challenge.id, newStatus).subscribe({
          next: () => {
            challenge.is_active = newStatus;
            this.helper.modalSuccess(`¡Challenge ${newStatus ? 'activado' : 'desactivado'} exitosamente!`);
          },
          error: (error) => {
            console.error('Error updating status:', error);
            this.helper.modalError('Error al actualizar el estado del challenge');
          }
        });
      }
    });
  }

  /**
   * Manejo de selección de imagen
   */
  onImageSelected(event: any) {
    console.log('🎯 onImageSelected called with event:', event);

    let file: File | null = null;

    // Manejar evento de drag & drop
    if (event.type === 'drop') {
      console.log('📁 Drop event detected');
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        file = files[0];
      }
    }
    // Manejar evento de input file
    else if (event.target && event.target.files) {
      console.log('📁 File input event detected');
      file = event.target.files[0];
    }

    if (!file) {
      console.log('❌ No se encontró archivo en el evento');
      console.log('Event object:', event);
      return;
    }

    console.log('📁 Archivo encontrado:', file.name, file.type, file.size);

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      this.imageError = 'Solo se permiten archivos de imagen';
      console.log('❌ Error: tipo de archivo no válido');
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      this.imageError = 'La imagen debe ser menor a 2MB';
      console.log('❌ Error: archivo muy grande');
      return;
    }

    this.selectedImage = file;
    this.imageError = '';

    console.log('✅ Archivo válido, iniciando conversión a base64...');

    // Convertir a base64 y crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = (e.target as FileReader).result as string;
      console.log('📸 FileReader onload - resultado recibido');
      console.log('📸 Resultado completo (primeros 100 chars):', result ? result.substring(0, 100) : 'null');

      if (!result) {
        console.log('❌ FileReader resultado vacío');
        this.imageError = 'Error al procesar la imagen';
        return;
      }

      // Guardar la imagen completa en base64 para el preview Y para enviar
      this.imagePreview = result;
      this.selectedImageBase64 = result; // ¡CAMBIO IMPORTANTE! Guardar el string completo

      console.log('🖼️ imagePreview asignado:', !!this.imagePreview);
      console.log('✅ selectedImageBase64 asignado con prefijo completo');
      console.log('📏 Base64 length:', result ? result.length : 0);
      console.log('📸 Base64 sample:', result ? result.substring(0, 100) + '...' : 'null');
    };

    reader.onerror = (error) => {
      console.error('❌ Error al leer archivo:', error);
      this.imageError = 'Error al procesar la imagen';
      this.selectedImageBase64 = null;
      this.imagePreview = null;
    };

    console.log('📖 Iniciando FileReader.readAsDataURL...');
    reader.readAsDataURL(file);
  }

  /**
   * Remover imagen seleccionada
   */
  removeImage() {
    this.selectedImage = null;
    this.selectedImageBase64 = null;
    this.imagePreview = null;
    this.imageError = '';
  }

  /**
   * Obtener texto del tipo de challenge
   */
  getChallengeTypeText(type: string): string {
    const option = this.challengeTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  }

  /**
   * Obtener clase CSS para el estado
   */
  getStatusClass(challenge: any): string {
    return challenge.is_active ? 'text-success' : 'text-danger';
  }

  /**
   * Formatear fecha
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  }

  /**
   * Obtener clase CSS para el tipo de challenge
   */
  getChallengeTypeClass(type: string): string {
    switch (type) {
      case 'referrals':
      case 'effective_referrals':
        return 'text-primary';
      case 'points':
        return 'text-warning';
      case 'formulas':
        return 'text-info';
      case 'login_streak':
        return 'text-success';
      case 'surveys':
        return 'text-secondary';
      case 'achievements':
        return 'text-danger';
      default:
        return 'text-muted';
    }
  }
}
